<script setup>
import { t } from '../i18n';

const emit = defineEmits(['login', 'register']);

// Inline icons keep the portfolio preview independent of external image loading.
const PREVIEW_ITEMS = [
  { key: 'portfolio', icon: 'm3.5 13 4-4 2.5 2.5 5-6' },
  { key: 'payouts', icon: 'M4 5.5h12v10H4zM4 8.5h12M7 3.5v4m6-4v4' },
  { key: 'analytics', icon: 'M10 3.5a6.5 6.5 0 1 0 6.5 6.5H10zM12.5 3.8A6.5 6.5 0 0 1 16.2 7h-3.7z' },
];
</script>

<template>
  <section class="mx-auto flex w-full max-w-5xl flex-1 items-center py-5 sm:py-10 lg:py-16">
    <div class="grid w-full items-center gap-10 lg:grid-cols-[minmax(0,0.9fr)_minmax(22rem,0.75fr)] lg:gap-16">
      <div class="min-w-0">
        <p class="mb-4 text-sm font-medium text-emerald-400 sm:text-base">
          {{ t('guestLanding.eyebrow') }}
        </p>
        <h1 class="max-w-xl text-4xl font-semibold tracking-[-0.04em] text-slate-50 sm:text-5xl lg:text-6xl">
          {{ t('guestLanding.title') }}
        </h1>
        <p class="mt-5 max-w-lg text-base leading-7 text-slate-400 sm:text-lg">
          {{ t('guestLanding.description') }}
        </p>

        <div class="mt-8 flex flex-col gap-3 sm:mt-10 sm:flex-row sm:items-center">
          <button
            type="button"
            class="min-h-11 rounded-xl bg-emerald-500 px-5 py-3 text-sm font-semibold text-slate-950 transition hover:bg-emerald-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950"
            @click="emit('register')"
          >
            {{ t('guestLanding.createAccount') }}
          </button>
          <button
            type="button"
            class="min-h-11 rounded-xl px-4 py-3 text-sm font-medium text-slate-300 transition hover:bg-slate-900 hover:text-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-500"
            @click="emit('login')"
          >
            {{ t('guestLanding.hasAccount') }}
          </button>
        </div>

        <ul class="mt-10 grid gap-3 text-sm leading-5 text-slate-400 sm:grid-cols-3 lg:mt-14 lg:grid-cols-2">
          <li
            v-for="feature in ['capital', 'payouts', 'structure', 'achievements', 'strategies', 'news']"
            :key="feature"
            class="flex items-start gap-2"
          >
            <svg
              class="mt-0.5 size-4 shrink-0 text-emerald-400"
              viewBox="0 0 16 16"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="m3.25 8.25 2.75 2.5 6.75-6"
                stroke="currentColor"
                stroke-width="1.75"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            <span>{{ t(`guestLanding.feature.${feature}`) }}</span>
          </li>
        </ul>
      </div>

      <div class="relative overflow-hidden rounded-3xl border border-slate-700/80 bg-slate-900 p-5 shadow-2xl shadow-black/20 sm:p-7">
        <!-- Decorative only: the actual portfolio chart remains available after sign-in. -->
        <svg
          class="pointer-events-none absolute inset-x-0 top-1 h-[68%] w-full opacity-80"
          viewBox="0 0 500 240"
          preserveAspectRatio="none"
          fill="none"
          aria-hidden="true"
        >
          <defs>
            <linearGradient
              id="guest-chart-area"
              x1="0"
              y1="0"
              x2="0"
              y2="1"
            >
              <stop
                offset="0"
                stop-color="#10b981"
                stop-opacity="0.24"
              />
              <stop
                offset="1"
                stop-color="#10b981"
                stop-opacity="0"
              />
            </linearGradient>
            <filter
              id="guest-chart-glow"
              x="-20%"
              y="-30%"
              width="140%"
              height="160%"
            >
              <feGaussianBlur stdDeviation="4" />
            </filter>
          </defs>
          <path
            d="M0 198 C42 190 46 155 86 163 S130 187 159 136 S203 128 232 143 S279 112 302 116 S352 94 376 105 S419 66 450 74 S477 48 500 18 V240 H0Z"
            fill="url(#guest-chart-area)"
          />
          <path
            d="M0 198 C42 190 46 155 86 163 S130 187 159 136 S203 128 232 143 S279 112 302 116 S352 94 376 105 S419 66 450 74 S477 48 500 18"
            stroke="#10b981"
            stroke-width="10"
            stroke-linecap="round"
            filter="url(#guest-chart-glow)"
            opacity="0.32"
          />
          <path
            d="M0 198 C42 190 46 155 86 163 S130 187 159 136 S203 128 232 143 S279 112 302 116 S352 94 376 105 S419 66 450 74 S477 48 500 18"
            stroke="#34d399"
            stroke-width="2.5"
            stroke-linecap="round"
          />
        </svg>

        <div class="relative z-10">
          <p class="text-sm font-medium text-slate-400">
            {{ t('guestLanding.cardTitle') }}
          </p>
          <p class="mt-2 tabular-nums text-3xl font-semibold tracking-[-0.035em] text-slate-50 sm:text-4xl">
            1 248 650 ₽
          </p>
          <p class="mt-1 text-sm font-medium text-emerald-400">
            +2,34 % {{ t('guestLanding.cardPeriod') }}
          </p>

          <div class="mt-7 grid grid-cols-3 gap-2 border-t border-slate-700/80 pt-5 text-center">
            <div
              v-for="item in PREVIEW_ITEMS"
              :key="item.key"
              class="min-w-0"
            >
              <svg
                class="mx-auto mb-2 size-7 rounded-full bg-slate-800/90 p-1.5 text-emerald-400"
                viewBox="0 0 20 20"
                fill="none"
                aria-hidden="true"
              >
                <path
                  :d="item.icon"
                  stroke="currentColor"
                  stroke-width="1.35"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              <p class="truncate text-xs text-slate-400">
                {{ t(`guestLanding.card.${item.key}`) }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
